import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect } from 'react';
import { SubmitHandler, useForm, UseFormTrigger } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import { ProgramData, programResolver } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

import AddAnotherButton from '../buttons/AddAnotherButton';
import BrandedButton from '../buttons/BrandedButton';
import DatePickerInputField from '../inputs/DateInputPickerField';
import GoogleAutoComplete from '../inputs/GoogleAutocomplete';
import TextInputField from '../inputs/TextInputField';

interface ProgramFormProps {
  initialData?: ProgramData;
  showDoneButton: boolean;
  showBackButton?: boolean;
  onAdd?: (data: ProgramData) => void;
  onDone?: (data: ProgramData, trigger: UseFormTrigger<ProgramData>) => void;
  manualValidation?: boolean;
  doneLabel?: string;
  isLoading?: boolean;
}

const ProgramForm = ({
  initialData,
  showDoneButton,
  showBackButton = false,
  onDone,
  manualValidation = false,
  onAdd,
  doneLabel = i18n.t('mix.done'),
  isLoading = false
}: ProgramFormProps) => {
  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    setFocus,
    reset,
    trigger,
    formState: { isValid, errors, submitCount }
  } = useForm<ProgramData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: programResolver as any
  });

  const onSubmit: SubmitHandler<ProgramData> = async (data) => {
    if (onDone) {
      onDone(data, trigger);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <TextInputField
          name="name"
          control={control}
          placeholder={i18n.t('sign_in.nonProfit.programs.name_placeholder')}
          label={i18n.t('sign_in.nonProfit.programs.name_label')}
          returnKeyLabel="next"
          returnKeyType="next"
          onSubmitEditing={() => setFocus('description')}
        />

        <TextInputField
          name="description"
          multiline
          control={control}
          placeholder={i18n.t('sign_in.nonProfit.programs.description_placeholder')}
          label={i18n.t('sign_in.nonProfit.programs.description_label')}
          maxHeight={80}
        />

        <DatePickerInputField
          control={control}
          name="programDate"
          label={i18n.t('sign_in.nonProfit.programs.date_label')}
        />

        <TextInputField
          name="budget"
          control={control}
          label={i18n.t('sign_in.nonProfit.programs.budget_label')}
          placeholder={i18n.t('sign_in.nonProfit.programs.budget_placeholder')}
          type="numeric"
          currency
          returnKeyType="done"
          onSubmitEditing={() => setFocus('address')}
        />

        <View style={styles.autoComplete}>
          <GoogleAutoComplete
            initialValue={initialData?.address ?? undefined}
            type="address"
            label={i18n.t('profile.create_form.address_label')}
            placeholder={i18n.t('sign_in.nonProfit.programs.location_placeholder')}
            predictionsScrollEnabled={false}
            onChange={(result) => {
              if (result) {
                setValue('location', `${result.lat},${result.lng}`);
                setValue('address', result.formattedAddress);
              } else {
                setValue('location', undefined);
                setValue('address', undefined);
              }
            }}
          />
        </View>

        {onAdd && (
          <AddAnotherButton
            onPress={() => {
              trigger().then((valid) => {
                if (valid) onAdd(getValues());
              });
            }}
            label={i18n.t('sign_in.nonProfit.programs.submit_and_add')}
          />
        )}
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      {showDoneButton && (
        <View style={styles.nextBackContainer}>
          {showBackButton && router.canGoBack() && (
            <BrandedButton style={styles.flexOne} disabled={isLoading} onPress={router.back}>
              {capitalize(i18n.t('mix.back'))}
            </BrandedButton>
          )}
          <BrandedButton
            style={styles.flexOne}
            onPress={() => {
              if (manualValidation) {
                onSubmit(getValues());
              } else {
                trigger().then((valid) => {
                  if (valid) onSubmit(getValues());
                });
              }
            }}
            disabled={(!isValid && submitCount > 0) || isLoading}
            loading={isLoading}>
            {doneLabel}
          </BrandedButton>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    flex: 1,
    gap: 15
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  autoComplete: {
    zIndex: 1
  }
});

export default ProgramForm;
